// Severity Styles
// ----------------
// Single source of truth for the visual treatment of a vulnerability's
// risk rating across the app — Tailwind class triplets for the five
// shapes severity appears in (pill / tile / dot / filter-pill / hex
// for inline-styled HTML reports).
//
// Was previously inlined as parallel maps + ad-hoc class strings at
// eight different render sites (Dashboard severity pills, Applications
// row pills, TestDetails Findings tiles, Dashboard summary tiles,
// VulnerabilityTable filter pills + header row, SingleVulnRow risk
// dot, Vulnerability detail page risk block, report generator). Each
// site picked slightly different shades — bg-red-50 vs bg-red-100 vs
// bg-red-50/70 — making it easy for a new feature to land on the
// wrong shade for its context. Centralised here so every surface
// picks the same shade for the same severity in the same role.

import {
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
} from "@/constants/definitions";

// Resolve any-cased / aliased risk rating value to one of the
// canonical RISK_RATING_* constants. Lookups below are keyed by these.
function normalizeSeverity(
  riskRating: string | undefined | null,
): string {
  if (!riskRating) return RISK_RATING_INFO;
  const upper = riskRating.toUpperCase();
  if (upper === RISK_RATING_CRITICAL.toUpperCase()) return RISK_RATING_CRITICAL;
  if (upper === RISK_RATING_HIGH.toUpperCase()) return RISK_RATING_HIGH;
  if (upper === RISK_RATING_MED.toUpperCase()) return RISK_RATING_MED;
  if (upper === RISK_RATING_LOW.toUpperCase()) return RISK_RATING_LOW;
  if (upper === RISK_RATING_INFO.toUpperCase()) return RISK_RATING_INFO;
  return RISK_RATING_INFO;
}

export function getSeverityLabel(
  riskRating: string | undefined | null,
): string {
  const canonical = normalizeSeverity(riskRating);
  if (canonical === RISK_RATING_CRITICAL) return "Critical";
  if (canonical === RISK_RATING_HIGH) return "High";
  if (canonical === RISK_RATING_MED) return "Medium";
  if (canonical === RISK_RATING_LOW) return "Low";
  return "Info";
}

// --------------------------------------------------------
// Pill — small filled badge. "{N} Critical" on Dashboard /
// Applications cards, "Critical" badge in Dashboard's Open
// Vulnerabilities row. Strength 100/700–800.
// --------------------------------------------------------
export interface SeverityPillStyle {
  bg: string;
  text: string;
}
const PILL_STYLES: Record<string, SeverityPillStyle> = {
  [RISK_RATING_CRITICAL]: { bg: "bg-red-100",    text: "text-red-800" },
  [RISK_RATING_HIGH]:     { bg: "bg-orange-100", text: "text-orange-700" },
  [RISK_RATING_MED]:      { bg: "bg-amber-100",  text: "text-amber-700" },
  [RISK_RATING_LOW]:      { bg: "bg-yellow-100", text: "text-yellow-700" },
  [RISK_RATING_INFO]:     { bg: "bg-sky-100",    text: "text-sky-700" },
};
export function getSeverityPillStyle(
  riskRating: string | undefined | null,
): SeverityPillStyle {
  return PILL_STYLES[normalizeSeverity(riskRating)];
}

// --------------------------------------------------------
// Tile — large box with big number + small label. TestDetails
// Findings tiles, Dashboard summary tiles, Vulnerability detail
// risk block. Strength 50/600–700/400.
//
// hoverBg + ring are populated for the TestDetails Findings
// tiles which double as severity-filter buttons; the dashboard
// summary tiles and vuln detail block ignore those fields.
// --------------------------------------------------------
export interface SeverityTileStyle {
  bg: string;
  hoverBg: string;
  ring: string;
  numberText: string;
  labelText: string;
}
const TILE_STYLES: Record<string, SeverityTileStyle> = {
  [RISK_RATING_CRITICAL]: { bg: "bg-red-50",    hoverBg: "hover:bg-red-100",    ring: "ring-red-500",    numberText: "text-red-700",    labelText: "text-red-400" },
  [RISK_RATING_HIGH]:     { bg: "bg-orange-50", hoverBg: "hover:bg-orange-100", ring: "ring-orange-500", numberText: "text-orange-600", labelText: "text-orange-400" },
  [RISK_RATING_MED]:      { bg: "bg-amber-50",  hoverBg: "hover:bg-amber-100",  ring: "ring-amber-500",  numberText: "text-amber-600",  labelText: "text-amber-400" },
  [RISK_RATING_LOW]:      { bg: "bg-yellow-50", hoverBg: "hover:bg-yellow-100", ring: "ring-yellow-500", numberText: "text-yellow-500", labelText: "text-yellow-500" },
  [RISK_RATING_INFO]:     { bg: "bg-sky-50",    hoverBg: "hover:bg-sky-100",    ring: "ring-sky-500",    numberText: "text-sky-600",    labelText: "text-sky-400" },
};
export function getSeverityTileStyle(
  riskRating: string | undefined | null,
): SeverityTileStyle {
  return TILE_STYLES[normalizeSeverity(riskRating)];
}

// --------------------------------------------------------
// Dot — risk indicator + label. SingleVulnRow's left cell.
// Strength 700/500 (dot) + 700–800 (label).
// --------------------------------------------------------
export interface SeverityDotStyle {
  dot: string;
  label: string;
}
const DOT_STYLES: Record<string, SeverityDotStyle> = {
  [RISK_RATING_CRITICAL]: { dot: "bg-red-700",    label: "text-red-800" },
  [RISK_RATING_HIGH]:     { dot: "bg-orange-600", label: "text-orange-700" },
  [RISK_RATING_MED]:      { dot: "bg-amber-500",  label: "text-amber-700" },
  [RISK_RATING_LOW]:      { dot: "bg-yellow-500", label: "text-yellow-700" },
  [RISK_RATING_INFO]:     { dot: "bg-gray-400",   label: "text-gray-600" },
};
export function getSeverityDotStyle(
  riskRating: string | undefined | null,
): SeverityDotStyle {
  return DOT_STYLES[normalizeSeverity(riskRating)];
}

// --------------------------------------------------------
// Filter pill (active state) — VulnerabilityTable's per-severity
// filter buttons. Strength bright/500–700 + white text.
// --------------------------------------------------------
const FILTER_ACTIVE_STYLES: Record<string, string> = {
  [RISK_RATING_CRITICAL]: "bg-red-700 text-white",
  [RISK_RATING_HIGH]:     "bg-orange-600 text-white",
  [RISK_RATING_MED]:      "bg-amber-500 text-white",
  [RISK_RATING_LOW]:      "bg-yellow-500 text-white",
  [RISK_RATING_INFO]:     "bg-sky-500 text-white",
};
export function getSeverityFilterActiveClass(
  riskRating: string | undefined | null,
): string {
  return FILTER_ACTIVE_STYLES[normalizeSeverity(riskRating)];
}

// --------------------------------------------------------
// Hex — for inline-styled HTML (report generator). Pre-rendered
// reports can't reference Tailwind utility classes; they need
// concrete hex colors embedded in style="...".
// --------------------------------------------------------
export interface SeverityHexColors {
  bg: string;
  text: string;
  border: string;
}
export const SEVERITY_HEX_COLORS: Record<string, SeverityHexColors> = {
  [RISK_RATING_CRITICAL]: { bg: "#fef2f2", text: "#b91c1c", border: "#fca5a5" },
  [RISK_RATING_HIGH]:     { bg: "#fff7ed", text: "#c2410c", border: "#fdba74" },
  [RISK_RATING_MED]:      { bg: "#fffbeb", text: "#b45309", border: "#fcd34d" },
  [RISK_RATING_LOW]:      { bg: "#fefce8", text: "#a16207", border: "#fde047" },
  [RISK_RATING_INFO]:     { bg: "#f0f9ff", text: "#0369a1", border: "#7dd3fc" },
};
